/**
 * Wrap / unwrap helpers for the dispatch (mobile-originated) XML envelope.
 *
 * 同 scheduledTaskWrapper 的范式 —— 手机派来的任务在送进 SDK 前包一层
 * `<dispatch>` + 自治 preamble,告诉 LLM:
 *
 *   - 当前会话由手机端发起,用户在路上 / 短停留 —— 回复尽量直接、口语、不长
 *   - 不要 AskUserQuestion 反复确认细节;含糊的地方自己挑个合理选项,注明
 *     "我按 X 处理了"即可,真要改主意桌面再说
 *   - 改文件 / 跑 bash 等需要权限的动作仍走 PermissionRouter 双端确认
 *     (那是 SDK 的内置 gate,不在这层 prompt 里管)
 *
 * 渲染层和 scheduled-task 一样：renderer 检测 `<dispatch>` wrapper → 显示
 * 一个 "From mobile" pill,而不是把 XML 摊在气泡里。手机端的 transcript
 * 由 task_new 事件 payload 派生(那里是原始 prompt,不含 wrapper),不受影响。
 */

/** 自治 preamble。verbatim 配合 SCHEDULED_TASK_AUTONOMY_PREAMBLE 风格写。 */
export const DISPATCH_AUTONOMY_PREAMBLE =
  'This task came from the user\'s phone. They are away from the desktop ' +
  '— keep the reply short, conversational, and decisive. Do NOT use ' +
  'AskUserQuestion to clarify minor details; pick a reasonable default, ' +
  'proceed, and note what you assumed in your final reply (e.g. "I ' +
  'assumed X — let me know if you wanted Y"). Long code blocks and large ' +
  'tables read poorly on a phone; prefer a 1–3 sentence summary plus a ' +
  'concrete next step. Tools that mutate files / run shell commands still ' +
  'go through the desktop permission gate as usual — that is handled ' +
  'outside this prompt.'

/** Build the `<dispatch>...</dispatch>` string sent to the SDK. */
export function wrapDispatchTask(body: string): string {
  return (
    `<dispatch>\n` +
    `${DISPATCH_AUTONOMY_PREAMBLE}\n\n` +
    `${body}\n` +
    `</dispatch>`
  )
}

const WRAPPER_RE = /^<dispatch>\n([\s\S]*?)\n<\/dispatch>$/

/** Returns `{ body }` if wrapped, else null. */
export function unwrapDispatchTask(text: string): { body: string } | null {
  if (typeof text !== 'string') return null
  const m = WRAPPER_RE.exec(text.trim())
  if (!m) return null
  let body = m[1]
  if (body.startsWith(DISPATCH_AUTONOMY_PREAMBLE)) {
    body = body.slice(DISPATCH_AUTONOMY_PREAMBLE.length).replace(/^\n+/, '')
  }
  return { body }
}

/** Cheap detection for the renderer's pill detector. */
export function isDispatchWrapped(text: string): boolean {
  return unwrapDispatchTask(text) !== null
}
